/**
 * Standard 1-D random-walk Kalman filter, Rauch–Tung–Striebel smoother, and
 * EM parameter estimation used by the timeseries feature.
 */

export type Observation = number | null | undefined;

export interface KalmanArraysResult {
  /** Filtered state mean. */
  xHat: number[];
  /** Filtered state variance. */
  P: number[];
  /** One-step-ahead predicted mean (before measurement update). */
  xPred: number[];
  /** One-step-ahead predicted variance (before measurement update). */
  pPred: number[];
}

export interface KalmanSmootherResult {
  xSmooth: number[];
  PSmooth: number[];
  /** Lag-1 smoothed covariance between t and t+1 (length n-1). */
  covSS: number[];
}

export interface KalmanParams {
  Q: number;
  R: number;
}

export interface KalmanFilterResult {
  mean: number[];
  variance: number[];
}

function firstFinite(values: readonly Observation[]): number {
  for (const v of values) {
    if (v != null && Number.isFinite(v)) return v;
  }
  return 0;
}

/**
 * Forward pass of the Kalman filter.
 *
 * @param data      Observations (null/undefined for missing values).
 * @param Q         Process noise variance.
 * @param R         Measurement noise variance.
 * @param counts    Optional observation counts per step; `R_t = R / counts[t]`
 *                  when provided and positive.
 */
export function kalmanFilterArrays(
  data: readonly Observation[],
  Q: number,
  R: number,
  counts?: readonly number[],
): KalmanArraysResult {
  const n = data.length;
  const xHat = new Array<number>(n).fill(0);
  const P = new Array<number>(n).fill(0);
  const xPred = new Array<number>(n).fill(0);
  const pPred = new Array<number>(n).fill(0);

  let x = firstFinite(data);
  let p = 1;

  for (let i = 0; i < n; i++) {
    xPred[i] = x;
    pPred[i] = p + Q;
    const z = data[i];
    if (z != null && Number.isFinite(z)) {
      const Ri = counts && counts[i] != null && counts[i]! > 0 ? R / counts[i]! : R;
      const K = pPred[i]! / (pPred[i]! + Ri);
      x = xPred[i]! + K * (z - xPred[i]!);
      p = (1 - K) * pPred[i]!;
    } else {
      x = xPred[i]!;
      p = pPred[i]!;
    }
    xHat[i] = x;
    P[i] = p;
  }

  return { xHat, P, xPred, pPred };
}

/** Rauch–Tung–Striebel smoother over the filter output. */
export function rtsSmoother(
  xHat: readonly number[],
  P: readonly number[],
  xPred: readonly number[],
  pPred: readonly number[],
): KalmanSmootherResult {
  const n = xHat.length;
  const xSmooth = new Array<number>(n);
  const PSmooth = new Array<number>(n);
  const covSS = new Array<number>(Math.max(0, n - 1));

  xSmooth[n - 1] = xHat[n - 1]!;
  PSmooth[n - 1] = P[n - 1]!;

  for (let t = n - 2; t >= 0; t--) {
    const A = P[t]! / pPred[t + 1]!;
    xSmooth[t] = xHat[t]! + A * (xSmooth[t + 1]! - xPred[t + 1]!);
    PSmooth[t] = P[t]! + A * (PSmooth[t + 1]! - pPred[t + 1]!) * A;
    covSS[t] = A * PSmooth[t + 1]!;
  }

  return { xSmooth, PSmooth, covSS };
}

export interface EstimateKalmanParamsOptions {
  QInit?: number;
  RInit?: number;
  maxIters?: number;
  counts?: readonly number[];
}

/** EM-based estimator for `Q`, `R` on a random-walk + noise model. */
export function estimateKalmanParams(
  data: readonly Observation[],
  options: EstimateKalmanParamsOptions = {},
): KalmanParams {
  const { QInit = 75e-6, RInit = 0.0065, maxIters = 10, counts } = options;
  let Q = QInit;
  let R = RInit;
  const n = data.length;
  if (n === 0) return { Q, R };

  for (let iter = 0; iter < maxIters; iter++) {
    const filterResult = kalmanFilterArrays(data, Q, R, counts);
    const smoother = rtsSmoother(
      filterResult.xHat,
      filterResult.P,
      filterResult.xPred,
      filterResult.pPred,
    );
    const { xSmooth, PSmooth, covSS } = smoother;

    let sumQ = 0;
    for (let t = 0; t < n - 1; t++) {
      const diff = xSmooth[t + 1]! - xSmooth[t]!;
      sumQ += diff * diff + PSmooth[t + 1]! + PSmooth[t]! - 2 * covSS[t]!;
    }
    const newQ = n > 1 ? sumQ / (n - 1) : Q;

    let sumR = 0;
    let countZ = 0;
    for (let t = 0; t < n; t++) {
      const z = data[t];
      if (z != null && Number.isFinite(z)) {
        const err = z - xSmooth[t]!;
        const ci = counts && counts[t] != null && counts[t]! > 0 ? counts[t]! : 1;
        sumR += ci * (err * err + PSmooth[t]!);
        countZ += 1;
      }
    }
    const newR = countZ > 0 ? sumR / countZ : R;

    Q = newQ;
    R = newR;
  }

  return { Q, R };
}

/**
 * Convenience wrapper exposing `{ mean, variance }` without the one-step-ahead
 * prediction arrays.
 */
export function kalmanFilter(
  data: readonly Observation[],
  Q: number = 75e-6,
  R: number = 0.0065,
  counts?: readonly number[],
): KalmanFilterResult {
  const { xHat, P } = kalmanFilterArrays(data, Q, R, counts);
  return { mean: xHat, variance: P };
}

/**
 * How `Q` and `R` are chosen for a series:
 *   - `em`    — re-estimated per series via `estimateKalmanParams`.
 *   - `fixed` — the given constants, everywhere.
 * A new scheme (per-bank table, a different estimator, …) is a new union
 * member here plus a branch in `smoothSeries`.
 */
export type KalmanStrategy = { mode: 'em' } | { mode: 'fixed'; Q: number; R: number };

/**
 * The strategy every Kalman trend in the app runs under — the single knob
 * to turn. To fix R and Q app-wide, set
 * `{ mode: 'fixed', Q: <value>, R: <value> }`.
 *
 * Mirrored by hand in `backend/infra/math/kalman.py::KALMAN_STRATEGY`
 * (used by the nightly S3 export); keep the two in sync or the exported
 * CSVs and the on-screen trends will disagree.
 */
export const KALMAN_STRATEGY: KalmanStrategy = { mode: 'em' };

/**
 * Resolve `(Q, R)` under the active strategy, then run the filter. Every
 * chart that draws a Kalman trend goes through this — never call
 * `estimateKalmanParams` + `kalmanFilter` directly from a feature.
 */
export function smoothSeries(
  data: readonly Observation[],
  counts?: readonly number[],
  strategy: KalmanStrategy = KALMAN_STRATEGY,
): KalmanFilterResult {
  const { Q, R } = strategy.mode === 'fixed' ? strategy : estimateKalmanParams(data, { counts });
  return kalmanFilter(data, Q, R, counts);
}

/**
 * Inclusive list of ISO yyyy-mm-dd strings between `start` and `end`.
 * Used to align scores to a uniform daily grid before smoothing.
 */
export function calendarDays(start: string, end: string): string[] {
  const out: string[] = [];
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return out;
  }
  const day = new Date(startDate);
  // Normalise to UTC midnight so DST transitions don't cause duplicate or
  // missing days when iterating across them.
  day.setUTCHours(0, 0, 0, 0);
  const stop = new Date(endDate);
  stop.setUTCHours(0, 0, 0, 0);
  while (day.getTime() <= stop.getTime()) {
    out.push(day.toISOString().slice(0, 10));
    day.setUTCDate(day.getUTCDate() + 1);
  }
  return out;
}

interface DailyAggregate {
  scores: Array<number | null>;
  counts: number[];
}

/**
 * Aggregate raw `(date, score)` rows onto an explicit calendar-day axis.
 * Multi-document days collapse to the mean score with the count tracked
 * separately so the Kalman filter can weight `R` by `counts[i]`.
 */
export function buildDailyScoresAndCounts(
  allDays: ReadonlyArray<string>,
  records: ReadonlyArray<{ date: string; score: number | null }>,
): DailyAggregate {
  const sumByDay: Record<string, number> = Object.create(null);
  const countByDay: Record<string, number> = Object.create(null);

  for (const r of records) {
    if (r.score == null || !Number.isFinite(r.score)) continue;
    const day = r.date.slice(0, 10);
    sumByDay[day] = (sumByDay[day] ?? 0) + r.score;
    countByDay[day] = (countByDay[day] ?? 0) + 1;
  }

  const scores: Array<number | null> = new Array(allDays.length).fill(null);
  const counts: number[] = new Array(allDays.length).fill(0);
  for (let i = 0; i < allDays.length; i++) {
    const day = allDays[i]!;
    const c = countByDay[day] ?? 0;
    if (c > 0) {
      scores[i] = sumByDay[day]! / c;
      counts[i] = c;
    }
  }
  return { scores, counts };
}
